import * as React from 'react';
import Paper from '@material-ui/core/Paper';
import {
    EditingState, RowDetailState, SearchState, IntegratedFiltering, FilteringState,
    SortingState, IntegratedSorting, DataTypeProvider
} from '@devexpress/dx-react-grid';
import {
    Grid,
    Table,
    Toolbar,
    SearchPanel,
    TableHeaderRow,
    TableEditRow,
    TableEditColumn,
    TableRowDetail
} from '@devexpress/dx-react-grid-material-ui';

import {Query} from 'react-apollo'
import { GET_INSTRUMENTS_AND_HEADERS} from '../../apollo/queries/instruments'
import DeploymentTable from './DeploymentTable'
import StatusTable from './StatusTable'
import IssueTable from './IssueTable'
import CalibrationTable from './CalibrationTable'
import {withStyles} from '@material-ui/core/styles';
import StatusTimeline from '../../components/timelines/Status'
import LiveStatusTimeline from '../../components/timelines/LiveStatus'
import IssueTimeline from '../../components/timelines/Issue'
import CalibrationTimeline from '../../components/timelines/Calibration'
import LiveStatusDetail from './utils/LiveStatusDetail'
import HardwareTable from './HardwareTable'
import InstrumentSummary from './InstrumentSummary'
import {STATUS_SORTING, getCheckColor} from '../../constants'
import FilterPGN from './utils/FilterPGN'
import {NopsCheckDetail, RoutineQcCheckDetail} from './utils/QualityCheckDetail'


const getRowId = row => row.id

const styles = theme => ({
    selected: {
        //backgroundColor: theme.palette.background.paper,
        backgroundColor: 'whitesmoke',
    },
});

const SQUARE_SIZE = 20

const makeCheckSquare = (dateStr, checkType) => (
    <span
        title={dateStr || 'None'}
        style={{
            display: 'inline-block',
            width: `${SQUARE_SIZE}px`,
            height: `${SQUARE_SIZE}px`,
            borderRadius: '2px',
            marginRight: '3px',
            backgroundColor: getCheckColor(dateStr, checkType)
        }}
    />
)

const NopsCheckFormatter = ({row}) => (
    <span style={{display: 'flex', alignItems: 'center'}}>
        {makeCheckSquare(row.currentNopsCheck && row.currentNopsCheck.checkDate, 'nopsCheck')}
    </span>
)

const NopsCheckTypeProvider = props => (
    <DataTypeProvider
        formatterComponent={NopsCheckFormatter}
        {...props}
    />
)

const RqcCheckFormatter = ({row}) => (
    <span style={{display: 'flex', alignItems: 'center'}}>
        {makeCheckSquare(row.currentRqCheck && row.currentRqCheck.lastChecked, 'rqCheck')}
    </span>
)

const RqcCheckTypeProvider = props => (
    <DataTypeProvider
        formatterComponent={RqcCheckFormatter}
        {...props}
    />
)

const StatusFormatter = ({row}) => (<StatusTimeline data={row.stati} size={[SQUARE_SIZE, SQUARE_SIZE]}/>)

const StatusTypeProvider = props => (
    <DataTypeProvider
        formatterComponent={StatusFormatter}
        {...props}
    />
)

const LiveStatusFormatter = ({row}) => (<LiveStatusTimeline data={row.partialHeaders} locationMatch={row.headerLocationNotMatching} size={[SQUARE_SIZE, SQUARE_SIZE]}/>)

const LiveStatusTypeProvider = props => (
    <DataTypeProvider
        formatterComponent={LiveStatusFormatter}
        {...props}
    />
)


const CalibrationFormatter = ({row}) => (
    <CalibrationTimeline data={row.calibrations} size={[SQUARE_SIZE, SQUARE_SIZE]}/>)

const CalibrationTypeProvider = props => (
    <DataTypeProvider
        formatterComponent={CalibrationFormatter}
        {...props}
    />
)

const dateVal = v => {
    if (!v || v === 'None') return -Infinity
    const t = new Date(v).getTime()
    return isNaN(t) ? -Infinity : t
}

const sortingExtensions = [
    {
        columnName: "currentStatus",
        compare: (a, b) => (STATUS_SORTING[a] - STATUS_SORTING[b])
    },
    {
        columnName: "nopsCheck",
        compare: (a, b) => dateVal(a) - dateVal(b)
    },
    {
        columnName: "rqcCheck",
        compare: (a, b) => dateVal(a) - dateVal(b)
    }
]

const getHeaderLocation = headerData => {
    if (!headerData) {
        return null;
    }

    try {
        return JSON.parse(headerData)["LOCATION"];
    } catch (e) {
        return null;
    }
}


const stylesTable = theme => ({
    tableStriped: {
        '& tbody > tr:nth-of-type(odd)': {
            backgroundColor: '#f4f8ff',
        },
    },
});

const TableComponentBase = ({classes, ...restProps}) => (
    <Table.Table
        {...restProps}
        className={classes.tableStriped}
    />
);

const TableComponent = withStyles(stylesTable, {name: 'TableComponent'})(TableComponentBase);


class EditableTable extends React.PureComponent {

    state = {
        expandedRow: undefined,
        expandedColumn: undefined,
        filters: []
    }

    handleCellClick = (column, rowId) => {
        if ((column === "hardwarestatus" && this.props.user.hweditor) || column !== "hardwarestatus") {
            return () => {
                return column !== this.state.expandedColumn || rowId !== this.state.expandedRow ?
                    this.setState({expandedRow: rowId, expandedColumn: column})
                    : this.setState({expandedRow: undefined, expandedColumn: undefined})
            }
        } else {
            return () => undefined;
        }
    }

    Cell = (props) => {
        const {classes, ...other} = props
        const {column, row, value} = other;

        return (<Table.Cell {...other} onClick={this.handleCellClick(column.name, row.id)}
                            style={{cursor: "pointer", paddingTop: "0px", paddingBottom: "0px", borderBottom: "0px"}}
                            className={this.state.expandedColumn === column.name && this.state.expandedRow === row.id ? classes.selected : ""}/>
        );
    }

    StyledCell = withStyles(styles)(this.Cell)

    RowDetail = ({row}) => {
        switch (this.state.expandedColumn) {
            case "panId":
                return <InstrumentSummary data={row} user={this.props.user}/>
            case "isAt":
                return <DeploymentTable data={row.deployment} instrumentID={row.id} user={this.props.user}/>
            case "currentStatus":
                return <StatusTable data={row.stati} instrumentID={row.id} user={this.props.user}/>
            case "nopsCheck":
                return <NopsCheckDetail data={row}/>
            case "openIssues":
                return <IssueTable data={row.issues} instrumentID={row.id} user={this.props.user}/>
            case "calibrations":
                return <CalibrationTable data={row.calibrations} instrumentID={row.id} user={this.props.user}/>
            case "liveStatus":
                return <LiveStatusDetail data={row.partialHeaders}/>
            case "rqcCheck":
                return <RoutineQcCheckDetail data={row}/>
            case "hardwarestatus":
                return <HardwareTable data={row.hardwarestatus} instrumentID={row.id} user={this.props.user}/>
            default:
                return <p>nothing to see here</p>
        }
    }

    ContainerComponent = props => (<Table.Container {...props} style={{overflow: "visible"}}/>)
    ToggleComponent = props => (<Table.StubCell {...props} />)//toggleCellComponent={(props)=>(<Table.StubCell {...props} />)}

    render() {
        let data = this.props.data
        return (
            <Paper>
                <Grid
                    rows={data}
                    columns={this.props.columns}
                    getRowId={getRowId}
                    style={{overflow: "hidden"}}
                >
                    <StatusTypeProvider for={['currentStatus']}/>
                    <NopsCheckTypeProvider for={['nopsCheck']}/>
                    <LiveStatusTypeProvider for={['liveStatus']}/>
                    <RqcCheckTypeProvider for={['rqcCheck']}/>
                    <CalibrationTypeProvider for={['calibrations']}/>
                    <SortingState
                        defaultSorting={[{columnName: 'panId', direction: 'asc'}]}
                    />
                    <IntegratedSorting columnExtensions={sortingExtensions}/>
                    <SearchState defaultValue=""/>
                    <FilteringState
                        filters={this.state.filters}
                        //onFiltersChange={setFilters}
                    />
                    <IntegratedFiltering/>
                    <RowDetailState
                        expandedRowIds={this.state.expandedRow ? [this.state.expandedRow] : []}
                    />
                    <Table tableComponent={TableComponent} cellComponent={this.StyledCell}
                           containerComponent={this.ContainerComponent}
                    />
                    <TableHeaderRow showSortingControls/>
                    <Toolbar/>
                    <FilterPGN filtered={this.state.filters.length > 0}
                               toggleFilter={() => this.state.filters.length > 0 ? this.setState({filters: []}) : this.setState({
                                   filters: [{
                                       columnName: 'pgn',
                                       value: true
                                   }]
                               })}/>
                    <SearchPanel/>
                    <TableRowDetail
                        contentComponent={this.RowDetail}
                        toggleCellComponent={this.ToggleComponent}
                        toggleColumnWidth={0}
                    />
                </Grid>
            </Paper>
        );
    }
}

const getHardwareName = (hwStatus) => {
    var res = ""
    if (!hwStatus.data["instrument"]) {
        return res
    }
    if (hwStatus.data["instrument"].model) {
        res = res.concat(hwStatus.data["instrument"].model)
    }
    if (hwStatus.data["instrument.tracker"] && hwStatus.data["instrument.tracker"].model) {
        res = res.concat(" - " + hwStatus.data["instrument.tracker"].model)
    }
    if (hwStatus.data["instrument.enclosure.spec_dehumidifier"] && hwStatus.data["instrument.enclosure.spec_dehumidifier"].model) {
        res = res.concat(" - SDEH")
    }
    return (res)
}

const columns = [{
    name: 'panId',
    title: 'Pan ID'
},
    {
        name: 'isAt',
        title: 'Location',
        getCellValue: row => (row.deployment.length > 0 ? row.deployment[0].location.name : 'None')
    },
    {
        name: 'currentStatus',
        title: 'Status',
        getCellValue: row => (row.stati.length > 0 ? row.stati[0].status.name : 'None')
    },
    {
        name: 'nopsCheck',
        title: 'Nops check',
        getCellValue: row => (row.currentNopsCheck && row.currentNopsCheck.checkDate ? row.currentNopsCheck.checkDate : 'None')
    },
    {
        name: 'liveStatus',
        title: 'Live Status',
        getCellValue: row => (row.partialHeaders ? row.partialHeaders : 'None')
    },
    {
        name: 'rqcCheck',
        title: 'RQC check',
        getCellValue: row => (row.currentRqCheck && row.currentRqCheck.lastChecked ? row.currentRqCheck.lastChecked : 'None')
    },
    {
        name: 'openIssues',
        title: 'Issues',
        //getCellValue: row => (row.issues.length > 0 ? row.issues[0].issue.description : 'None')
        getCellValue: row => (<IssueTimeline data={row.issues} size={[90, 30]}/>)
    },
    {
        name: 'calibrations',
        title: 'Calibrations',
        getCellValue: row => (row.calibrations.length > 0 ? row.calibrations[0].data : 'None')
    },
    {
        name: 'hardwarestatus',
        title: 'Hardware',
        getCellValue: row => (row.hardwarestatus.length > 0 ? getHardwareName(row.hardwarestatus[0]) : 'None')
    },
    {
        name: 'networkOperator',
        title: 'Operator',
        getCellValue: row => (row.networkOperator && row.networkOperator.username)
    }]

const InstrumentView = (props) => {
    const now = new Date();
    const thirtyMinutesAgo = new Date(now.getTime() - 30 * 60 * 1000);
    const start = thirtyMinutesAgo.toISOString();
    const end = now.toISOString();
    return (
        <Query query={GET_INSTRUMENTS_AND_HEADERS} variables={{ start, end }} pollInterval={180000}>
            {({loading, error, data, networkStatus}) => {

                if (networkStatus !== 6 && loading) return <p>Loading...</p>;
                if (error) {
                    console.error("GET_INSTRUMENTS, error from instruments: ", error.message);
                    return <p>Error from Instuments</p>;
                }

                const instruments = data.instruments.map(instrument => {
                    const location = instrument.deployment && instrument.deployment[0] ? instrument.deployment[0].location : null
                    const headerData = (instrument.partialHeaders && instrument.partialHeaders[0]) ? instrument.partialHeaders[0].data : null
                    const headerLocation = getHeaderLocation(headerData)
                    const headerLocationNotMatching = !headerLocation || !location || location.name !== headerLocation

                    return {
                        ...instrument,
                        headerLocationNotMatching
                    }
                })

                return (
                    <EditableTable columns={columns}
                                   data={instruments} user={props.user}
                    />
                );

            }}
        </Query>
    )

}


export default InstrumentView
